/**
 * API クライアント
 *
 * 全リクエストを POST で送信し、レスポンスの code に応じてデータを返すか ApiError を throw する。
 * useAuthStore の token を自動注入する。
 * INVALID_TOKEN 時は WebView 環境なら自動ログインを 1 回試行し、成功すればリトライする。
 * 自動ログイン失敗時はログアウトし、WebView 内なら Native にも通知する。
 */
import { native } from "~/lib/nativeBridge";
import { useAuthStore } from "~/stores/useAuthStore";
import { API_ERROR_CODES, ApiError, type ApiResponse } from "~/types/api";
import { attemptAutoLogin, markAutoLoginExhausted } from "./autoLogin";

const BASE_URL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_API_URL;

async function executeRequest<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<ApiResponse<T>> {
  const token: string | null = useAuthStore.getState().token;

  const requestBody: Record<string, unknown> = {
    api: path,
    ...(token !== null ? { token } : {}),
    ...body,
  };

  let response: Response;
  try {
    response = await fetch(BASE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
  } catch {
    throw new ApiError(API_ERROR_CODES.UNKNOWN, "Network error");
  }

  // 502 はサーバー一時障害のため、エラーにせず空データとして扱う
  if (response.status === 502) {
    return { code: API_ERROR_CODES.SUCCESS, data: [] as unknown as T };
  }

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP error: ${response.status}`);
  }

  let json: ApiResponse<T>;
  try {
    json = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(API_ERROR_CODES.UNKNOWN, "Failed to parse response");
  }

  return json;
}

export async function apiClient<T>(
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const json = await executeRequest<T>(path, body);

  if (json.code === API_ERROR_CODES.SUCCESS) {
    return json.data;
  }

  if (json.code === API_ERROR_CODES.INVALID_TOKEN) {
    const recovered = await attemptAutoLogin();
    if (recovered) {
      // 新しいトークンでリトライ（1 回のみ）
      const retryJson = await executeRequest<T>(path, body);
      if (retryJson.code === API_ERROR_CODES.SUCCESS) {
        return retryJson.data;
      }
      // リトライが非 auth 系エラーの場合はログアウトせずそのエラーを投げる
      if (retryJson.code !== API_ERROR_CODES.INVALID_TOKEN) {
        throw new ApiError(retryJson.code, `API error: ${retryJson.code}`);
      }
    }
    // 自動ログイン失敗 or リトライも INVALID_TOKEN → 以降の自動ログインを抑止してログアウト
    markAutoLoginExhausted();
    useAuthStore.getState().logout();
    native.logout();
    throw new ApiError(API_ERROR_CODES.INVALID_TOKEN, "Invalid token");
  }

  throw new ApiError(json.code, `API error: ${json.code}`);
}
